<script setup lang="ts">
  import { ref } from 'vue'
  import { IconAdmin } from '@gat-ship/icons'
  import type { TenantUser } from '@gat-ship/types'
  import {
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection
  } from '@gat-ship/vue-components'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'

  const isOpen = ref(false)
  const tenantUserProvider = useTenantUserDetailContext()!
  const currentTenantUser = ref<TenantUser | undefined>(undefined)

  async function revokeTenantAdminPrivileges() {
    const tenantUser = currentTenantUser.value
    if (!tenantUser) return
    isOpen.value = false
    await tenantUserProvider.commands.revokeTenantAdminPrivileges({
      userId: tenantUser.userId
    })
  }

  export interface ShowRevokeTenantAdminModalParams {
    tenantUser: TenantUser
  }

  function showModal({ tenantUser }: ShowRevokeTenantAdminModalParams) {
    currentTenantUser.value = tenantUser
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <Modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconAdmin class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Revoke Tenant Admin Privileges
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="currentTenantUser">
        {{ currentTenantUser.fullName }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <p class="whitespace-pre-line text-sm leading-tight">
          Revoking tenant admin privileges removes this user's full
          administrative access to the entire tenant.
        </p>
        <p class="whitespace-pre-line text-sm leading-tight">
          Are you sure you want to revoke tenant admin privileges from this
          user?
        </p>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <Button variant="outline" @click="revokeTenantAdminPrivileges">
        Revoke Privileges
      </Button>
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </Modal>
</template>
